// src/pages/ParticipantConfirmationPage.js
import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAgreementByAgreementID, confirmAgreement, declineAgreement, confirmGroupParticipant } from '../firebase/services';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../hooks/useAuth';
import Navbar from '../components/Navbar';
import CircleIcon, { SvgIcon } from '../components/Icon';
import Lightbox from '../components/Lightbox';

const maskEmail = (email) => {
  if (!email || !email.includes('@')) return email || '';
  const [local, domain] = email.split('@');
  return local[0] + '•••@' + domain;
};

export default function ParticipantConfirmationPage() {
  const { agreementID } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [verifyError, setVerifyError] = useState(null);

  const { user } = useAuth();
  const verificationToken = searchParams.get('vt');

  useEffect(() => {
    getAgreementByAgreementID(agreementID).then(a => { setAgreement(a); setLoading(false); });
  }, [agreementID]);

  if (loading) return <div className="page"><Navbar /><div className="container" style={{ paddingTop: 32 }}>{t('confirmPage.loading')}</div></div>;
  if (!agreement) return <div className="page"><Navbar /><div className="container" style={{ paddingTop: 32 }}>{t('confirmPage.notFound')}</div></div>;

  const isGroup = agreement.participants && agreement.participants.length > 0;
  const unconfirmedParticipants = isGroup
    ? agreement.participants.filter(p => p.status !== 'Confirmed')
    : [];

  // Already confirmed — redirect directly
  if (agreement.status === 'Confirmed') {
    navigate(`/agreement-confirmed/${agreementID}`, { replace: true });
    return null;
  }

  // Already declined — redirect directly
  if (agreement.status === 'Declined') {
    navigate(`/agreement-declined/${agreementID}`, { replace: true });
    return null;
  }

  // Cancelled by creator
  if (agreement.status === 'Cancelled') {
    return (
      <div className="page">
        <Navbar />
        <div className="container" style={{ paddingTop: 28, paddingBottom: 60 }}>
          <div className="hero hero-declined">
            <div className="hero-icon" style={{ display: 'flex', justifyContent: 'center' }}><CircleIcon name="x" color="rgba(255,255,255,0.25)" size={52} iconSize={26} /></div>
            <div className="hero-title">{t('confirmPage.cancelledTitle')}</div>
            <div className="hero-sub">{t('confirmPage.cancelledSub')}</div>
          </div>
        </div>
      </div>
    );
  }

  // Group: all participants confirmed but status not yet updated
  if (isGroup && unconfirmedParticipants.length === 0 && agreement.status === 'Pending') {
    navigate(`/agreement-confirmed/${agreementID}`, { replace: true });
    return null;
  }

  const confirmName = isGroup ? selectedParticipant?.name : agreement.partnerName;

  const handleConfirm = async () => {
    if (!checked) return;
    setSubmitting(true);
    setVerifyError(null);
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (verificationToken && !isGroup) {
      try {
        const fns = getFunctions();
        const confirmFn = httpsCallable(fns, 'confirmVerified');
        await confirmFn({
          agreementId: agreementID,
          token: verificationToken,
          confirmerName: agreement.partnerName,
          timezone,
        });
        navigate(`/agreement-confirmed/${agreementID}`, { state: { justVerified: true } });
      } catch (err) {
        const msg = err?.message || '';
        if (msg.includes('expired')) setVerifyError('expired');
        else if (msg.includes('already-used')) setVerifyError('already-used');
        else setVerifyError('invalid');
        setSubmitting(false);
        return;
      }
    } else if (isGroup) {
      await confirmGroupParticipant(agreement.id, selectedParticipant.name, agreement.participants, user?.uid || null);
      navigate(`/agreement-confirmed/${agreementID}`);
    } else {
      await confirmAgreement(agreement.id, timezone, user?.uid || null);
      navigate(`/agreement-confirmed/${agreementID}`);
    }
  };

  const handleDecline = async () => {
    setSubmitting(true);
    await declineAgreement(agreement.id);
    navigate(`/agreement-declined/${agreementID}`);
  };

  return (
    <div className="page">
      <Navbar />
      <div className="container" style={{ paddingTop: 28, paddingBottom: 60 }}>
        <div className="page-title">{t('confirmPage.confirmOrDecline')}</div>
        <div className="page-subtitle">{t('confirmPage.reviewSubtitle')}</div>

        {/* Verified banner */}
        {verificationToken && !isGroup && agreement.participantEmail && !verifyError && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', marginBottom: 16,
            background: 'var(--confirmed-bg)', border: '1px solid var(--confirmed)', borderRadius: 10,
          }}>
            <SvgIcon name="shield" size={16} color="var(--confirmed)" strokeWidth={2}/>
            <span style={{ fontSize: '0.82rem', color: 'var(--confirmed)', fontWeight: 600, lineHeight: 1.4 }}>
              {t('confirmPage.verifiedBanner', { email: maskEmail(agreement.participantEmail) })}
            </span>
          </div>
        )}

        {/* Verify error messages */}
        {verifyError && (
          <div style={{
            padding: '12px 16px', marginBottom: 16,
            background: 'var(--declined-bg, #FEF2F2)', border: '1px solid var(--declined)', borderRadius: 10,
          }}>
            <p style={{ fontSize: '0.84rem', color: 'var(--declined)', fontWeight: 600, margin: 0 }}>
              {verifyError === 'expired' && t('confirmPage.expiredLink', { name: agreement.createdByName })}
              {verifyError === 'already-used' && t('confirmPage.alreadyUsedLink')}
              {verifyError === 'invalid' && t('confirmPage.invalidLink')}
            </p>
          </div>
        )}

        <div className="card">
          <div className="detail-row">
            <div className="detail-label">{t('confirmPage.agreementTitle')}</div>
            <div className="detail-value" style={{ fontFamily: 'Noto Sans, sans-serif', fontWeight: 700 }}>{agreement.title}</div>
          </div>
          <div className="detail-row">
            <div className="detail-label">{t('confirmPage.createdBy')}</div>
            <div className="detail-value">{agreement.createdByName}</div>
          </div>
          {agreement.amount && (
            <div className="detail-row">
              <div className="detail-label">{t('confirmPage.amount')}</div>
              <div className="detail-value" style={{ fontFamily: 'Noto Sans, sans-serif', fontWeight: 800, color: 'var(--primary)', fontSize: '1.1rem' }}>
                {agreement.currency} {agreement.amount}
              </div>
            </div>
          )}
          <div className="detail-row">
            <div className="detail-label">{t('confirmPage.details')}</div>
            <div className="detail-value" style={{ color: 'var(--muted)', fontSize: '0.86rem', lineHeight: 1.6 }}>{agreement.details}</div>
          </div>
          {agreement.deadline && (
            <div className="detail-row">
              <div className="detail-label">{t('confirmPage.dueDate')}</div>
              <div className="detail-value">{new Date(agreement.deadline).toLocaleDateString()}</div>
            </div>
          )}
          {agreement.evidence?.length > 0 && (
            <div>
              <div className="detail-label" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}><SvgIcon name="paperclip" size={13} color="var(--text)" /> {t('confirmPage.evidenceAttached')}</div>
              <div className="evidence-view-grid">
                {agreement.evidence.map((ev, i) =>
                  ev.type?.includes('pdf') ? (
                    <a key={i} href={ev.url} target="_blank" rel="noreferrer" className="evidence-view-thumb">
                      <SvgIcon name="doc" size={18} color="var(--text)" />
                    </a>
                  ) : (
                    <div key={i} className="evidence-view-thumb" style={{ cursor: 'pointer' }} onClick={() => setLightboxSrc(ev.url)}>
                      <img src={ev.url} alt="evidence" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {/* Group: show confirmation progress */}
          {isGroup && (
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 12 }}>
              <div className="detail-label" style={{ marginBottom: 8 }}>{t('confirmPage.participants')}</div>
              {agreement.participants.map((p, i) =>
                p.status === 'Confirmed' ? (
                  <div
                    key={i}
                    className="confirmed-row"
                    role="button"
                    tabIndex={0}
                    onClick={() => { navigate(`/agreement-confirmed/${agreementID}`); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/agreement-confirmed/${agreementID}`); }}
                    style={{ cursor: 'pointer', background: 'var(--confirmed-bg)', borderColor: 'var(--confirmed)' }}
                  >
                    <span style={{ color: 'var(--confirmed)', fontWeight: 600 }}>{p.name}</span>
                    <span className="badge badge-confirmed">{t('confirmPage.confirmedArrow')}</span>
                  </div>
                ) : (
                  <div key={i} className="confirmed-row">
                    <span>{p.name}</span>
                    <span className="badge badge-pending">{t('confirmPage.pending')}</span>
                  </div>
                )
              )}
            </div>
          )}
        </div>

        {/* Group: "Who are you?" picker */}
        {isGroup && !selectedParticipant && (
          <div className="card">
            <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: 10 }}>{t('confirmPage.whoAreYou')}</div>
            <p style={{ fontSize: '0.78rem', color: 'var(--muted)', marginBottom: 12 }}>{t('confirmPage.selectName')}</p>
            {unconfirmedParticipants.map((p, i) => (
              <button
                key={i}
                className="btn btn-secondary"
                style={{ textAlign: 'left', marginBottom: 6 }}
                onClick={() => setSelectedParticipant(p)}
              >
                {p.name}
              </button>
            ))}
            {agreement.participants.filter(p => p.status === 'Confirmed').map((p, i) => (
              <button
                key={`done-${i}`}
                className="btn btn-secondary"
                disabled
                style={{ textAlign: 'left', marginBottom: 6, opacity: 0.5 }}
              >
                {p.name} {t('confirmPage.alreadyConfirmed')}
              </button>
            ))}
          </div>
        )}

        {/* Show confirm/decline only when participant is selected (or non-group) */}
        {(!isGroup || selectedParticipant) && (
          <>
            {isGroup && selectedParticipant && (
              <div style={{ fontSize: '0.82rem', color: 'var(--primary)', fontWeight: 600, marginBottom: 10 }}>
                {t('confirmPage.confirmingAs', { name: selectedParticipant.name })}
                <button
                  onClick={() => { setSelectedParticipant(null); setChecked(false); }}
                  style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', marginLeft: 8, fontSize: '0.78rem' }}
                >
                  {t('confirmPage.change')}
                </button>
              </div>
            )}

            <div className="check-row">
              <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} id="confirm-check" />
              <label htmlFor="confirm-check" className="check-label">
                {t('confirmPage.iConfirm', { name: confirmName })}
              </label>
            </div>

            <button className="btn btn-primary" onClick={handleConfirm} disabled={!checked || submitting}>
              {submitting ? t('confirmPage.processing') : <><SvgIcon name="check" size={14} color="white" /> {t('confirmPage.confirmAgreement')}</>}
            </button>
            <button className="btn btn-danger" onClick={handleDecline} disabled={submitting}>
              <SvgIcon name="x" size={14} color="white" /> {t('confirmPage.declineAgreement')}
            </button>
          </>
        )}
      </div>
      {lightboxSrc && <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </div>
  );
}
